import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deleteRubric,
  fetchClassStudents,
  fetchExamDetail,
  uploadExamSubmission,
  uploadExamDSubmission,
  updateExamQuestions,
  approveQuestions,
  unapproveQuestions,
} from "../api/client";
import type { Paper, QuestionData, SessionDetail, StudentSummary } from "../api/types";
import {
  ArrowLeft,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileText,
  X,
  Loader2,
} from "lucide-react";

type SetupTab = "rubric" | "questions" | "papers";

// --- Upload lock helpers (localStorage-based, survives refresh) ---
const LOCK_TTL_MS = 60_000; // 60 seconds

function setUploadLock(sessionId: string, type: string) {
  localStorage.setItem(
    `upload_lock_${sessionId}`,
    JSON.stringify({ type, startedAt: Date.now() })
  );
}

function clearUploadLock(sessionId: string) {
  localStorage.removeItem(`upload_lock_${sessionId}`);
}

function getUploadLock(
  sessionId: string
): { type: string; startedAt: number } | null {
  try {
    const raw = localStorage.getItem(`upload_lock_${sessionId}`);
    if (!raw) return null;
    const lock = JSON.parse(raw);
    if (Date.now() - lock.startedAt > LOCK_TTL_MS) {
      localStorage.removeItem(`upload_lock_${sessionId}`);
      return null;
    }
    return lock;
  } catch {
    localStorage.removeItem(`upload_lock_${sessionId}`);
    return null;
  }
}

const SessionSetupPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('pages');
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [classStudents, setClassStudents] = useState<StudentSummary[]>([]);
  const [classStudentsStatus, setClassStudentsStatus] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null
  );
  const [uploading, setUploading] = useState(false);
  const [uploadLocked, setUploadLocked] = useState(() =>
    sessionId ? !!getUploadLock(sessionId) : false
  );
  const [activeTab, setActiveTab] = useState<SetupTab>("rubric");

  // Local editable state for questions tab
  const [localQuestions, setLocalQuestions] = useState<QuestionData[]>([]);
  const [questionsModified, setQuestionsModified] = useState(false);
  const [questionsSaving, setQuestionsSaving] = useState(false);
  const [questionsApproved, setQuestionsApproved] = useState(false);

  // Set active tab from query params on mount
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "questions" || tab === "papers") {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Poll for lock expiry (handles case where user refreshed mid-upload)
  useEffect(() => {
    if (!uploadLocked || !sessionId) return;
    const interval = setInterval(() => {
      const lock = getUploadLock(sessionId);
      if (!lock) {
        setUploadLocked(false);
        setUploading(false);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [uploadLocked, sessionId]);

  // Drag state
  const [rubricDragging, setRubricDragging] = useState(false);
  const [questionsDragging, setQuestionsDragging] = useState(false);
  const [papersDragging, setPapersDragging] = useState(false);

  const rubricInputRef = useRef<HTMLInputElement>(null);
  const questionsInputRef = useRef<HTMLInputElement>(null);
  const papersInputRef = useRef<HTMLInputElement>(null);

  // --- Data loading ---

  const load = () => {
    if (!sessionId) return;
    setStatus("loading");
    fetchExamDetail(sessionId)
      .then((data) => {
        setDetail(data);
        setStatus("ready");
        if (data.class_id) {
          setClassStudentsStatus("loading");
          fetchClassStudents(data.class_id)
            .then((rows) => {
              setClassStudents(rows);
              setClassStudentsStatus("loaded");
            })
            .catch((err) => {
              console.error("Could not load students:", err);
              setClassStudents([]);
              setClassStudentsStatus("error");
            });
        } else {
          setClassStudents([]);
          setClassStudentsStatus("idle");
        }
      })
      .catch((err) => {
        setError(err.message || t('sessionSetup.settingsLoadError'));
        setStatus("error");
      });
  };

  useEffect(() => {
    load();
  }, [sessionId]);

  // Sync localQuestions and questionsApproved whenever the authoritative detail changes
  useEffect(() => {
    if (detail) {
      setLocalQuestions(detail.questions.map(q => ({ ...q, sub_questions: q.sub_questions ? q.sub_questions.map(sq => ({ ...sq })) : [] })));
      setQuestionsModified(false);
      setQuestionsApproved(detail.questions_approved ?? false);
    }
  }, [detail]);

  const updateLocalQuestion = (idx: number, patch: Partial<QuestionData>) => {
    setLocalQuestions(prev => prev.map((q, i) => i === idx ? { ...q, ...patch } : q));
    setQuestionsModified(true);
  };

  const updateLocalSubQuestion = (
    qIdx: number,
    sqIdx: number,
    patch: Partial<{ label: string; text: string; maximum_points: number | null }>
  ) => {
    setLocalQuestions(prev =>
      prev.map((q, i) =>
        i !== qIdx ? q : {
          ...q,
          sub_questions: (q.sub_questions || []).map((sq, j) =>
            j !== sqIdx ? sq : { ...sq, ...patch }
          ),
        }
      )
    );
    setQuestionsModified(true);
  };

  const saveQuestions = async () => {
    if (!sessionId) return;
    setQuestionsSaving(true);
    try {
      await updateExamQuestions(sessionId, localQuestions);
      setQuestionsModified(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : t('sessionSetup.saveQuestionsError'));
    } finally {
      setQuestionsSaving(false);
    }
  };

  const studentsWithoutSubmission = useMemo(() => {
    if (!detail) return classStudents;
    const submittedIds = new Set(
      detail.papers
        .map((p) => p.student_id)
        .filter((id): id is number => Boolean(id))
    );
    return classStudents.filter((s) => !submittedIds.has(s.id));
  }, [classStudents, detail]);

  useEffect(() => {
    if (!studentsWithoutSubmission.length) {
      setSelectedStudentId(null);
      return;
    }
    if (
      !selectedStudentId ||
      !studentsWithoutSubmission.find((s) => s.id === selectedStudentId)
    ) {
      setSelectedStudentId(studentsWithoutSubmission[0].id);
    }
  }, [studentsWithoutSubmission, selectedStudentId]);

  // --- Legacy file operations (preserved) ---

  const acquireLock = useCallback(
    (type: string) => {
      if (!sessionId) return false;
      if (uploadLocked || uploading) return false;
      setUploadLock(sessionId, type);
      setUploadLocked(true);
      setUploading(true);
      return true;
    },
    [sessionId, uploadLocked, uploading]
  );

  const releaseLock = useCallback(() => {
    if (sessionId) clearUploadLock(sessionId);
    setUploadLocked(false);
    setUploading(false);
  }, [sessionId]);

  const uploadRubric = async (file: File) => {
    if (!sessionId) return;
    if (!acquireLock("rubric")) return;
    const fd = new FormData();
    fd.append("rubric_file", file);
    try {
      const res = await fetch(`/upload_rubric/${sessionId}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error === "insufficient_credits" ? t('common.insufficientCredits') : (data.error || t('sessionSetup.rubricLoadError')));
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('sessionSetup.rubricLoadError'));
    } finally {
      releaseLock();
    }
  };

  const removeRubric = async () => {
    if (!sessionId) return;
    if (!confirm(t('sessionSetup.rubricRemoveConfirm'))) return;
    await deleteRubric(Number(sessionId));
    load();
  };

  const uploadQuestionPaper = async (file: File) => {
    if (!sessionId) return;
    if (!acquireLock("questions")) return;
    const fd = new FormData();
    fd.append("question_paper", file);
    try {
      await fetch(`/clear_questions/${sessionId}`, { method: "POST" });
      const res = await fetch(`/upload_question_paper/${sessionId}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error === "insufficient_credits" ? t('common.insufficientCredits') : (data.error || t('sessionSetup.questionPaperLoadError')));
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('sessionSetup.questionPaperLoadError'));
    } finally {
      releaseLock();
    }
  };

  const clearQuestions = async () => {
    if (!sessionId) return;
    if (!confirm(t('sessionSetup.removeQuestionsConfirm'))) return;
    await fetch(`/clear_questions/${sessionId}`, { method: "POST" });
    load();
  };

  const handleApproveQuestions = async () => {
    if (!sessionId) return;
    try {
      await approveQuestions(Number(sessionId));
      setQuestionsApproved(true);
    } catch {
      alert(t('sessionSetup.approvalError'));
    }
  };

  const handleUnapproveQuestions = async () => {
    if (!sessionId) return;
    try {
      await unapproveQuestions(Number(sessionId));
      setQuestionsApproved(false);
    } catch {
      alert(t('sessionSetup.operationError'));
    }
  };

  const uploadForSelectedStudent = async (files: FileList | null) => {
    if (!files || !sessionId || !selectedStudentId) return;
    if (!acquireLock("papers")) return;
    try {
      if (detail?.grading_mode === "mod_d") {
        await uploadExamDSubmission(Number(sessionId), selectedStudentId, Array.from(files));
      } else if (files.length === 1) {
        await uploadExamSubmission(Number(sessionId), selectedStudentId, files[0]);
      } else {
        await uploadStudentBundle(files, selectedStudentId);
      }
      load();
    } catch (err) {
      alert(
      err instanceof Error ? err.message : t('sessionSetup.studentPaperLoadError')
    );
    } finally {
      releaseLock();
    }
  };

  const uploadStudentBundle = async (
    files: FileList | null,
    forcedStudentId?: number
  ) => {
    if (!files || !sessionId) return;
    const studentId = forcedStudentId || selectedStudentId || undefined;
    if (!studentId) {
      alert(t('sessionSetup.selectStudentFirst'));
      return;
    }

    const fd = new FormData();
    fd.append("student_id", String(studentId));
    Array.from(files).forEach((file) => fd.append("files[]", file));
    const res = await fetch(`/upload_student_pages/${sessionId}`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || t('sessionSetup.studentPagesLoadError'));
      return;
    }
    load();
  };

  const deletePaper = async (paperId: number) => {
    if (
      !confirm(t('sessionSetup.deletePaperConfirm'))
    )
      return;
    await fetch(`/delete_paper/${paperId}`, { method: "POST" });
    load();
  };

  // --- Drag handlers ---

  const handleRubricDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setRubricDragging(false);
    if (uploadLocked) return;
    const file = e.dataTransfer.files[0];
    if (file) uploadRubric(file);
  };

  const handleQuestionsDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setQuestionsDragging(false);
    if (uploadLocked) return;
    const file = e.dataTransfer.files[0];
    if (file) uploadQuestionPaper(file);
  };

  const handlePapersDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setPapersDragging(false);
    if (uploadLocked || !selectedStudentId) return;
    uploadForSelectedStudent(e.dataTransfer.files);
  };

  // --- Loading / error states ---

  if (status === "loading") {
    return (
      <div className="animate-fade-in">
        <p className="text-muted">{t('sessionSetup.loading')}</p>
      </div>
    );
  }

  if (status === "error" || !detail) {
    return (
      <div className="animate-fade-in">
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "var(--radius-md)",
            background: "var(--color-danger-bg)",
            color: "var(--color-danger)",
          }}
        >
          {error || t('sessionSetup.loadError')}
        </div>
      </div>
    );
  }

  const rubricFileId = detail.rubric_file_id || null;
  const hasRubric = Boolean(
    rubricFileId && rubricFileId !== "null" && rubricFileId !== "undefined"
  );

  const tabs: { key: SetupTab; label: string; count?: string }[] = [
    {
      key: "rubric",
      label: t('sessionSetup.tabRubric'),
      count: hasRubric ? "1" : undefined,
    },
    {
      key: "questions",
      label: t('sessionSetup.tabQuestions'),
      count: detail.questions.length ? String(detail.questions.length) : undefined,
    },
    {
      key: "papers",
      label: t('sessionSetup.tabPapers'),
      count: `${detail.papers.length}/${classStudents.length || detail.papers.length || 0}`,
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-16 mb-24">
        <button
          className="btn btn-outline"
          onClick={() =>
            navigate(
              detail.grading_mode === "mod_d"
                ? `/exams-d/${sessionId}`
                : detail.grading_mode === "mod_c"
                ? `/exams-c/${sessionId}`
                : `/exams/${sessionId}`
            )
          }
        >
          <ArrowLeft size={15} />
          {t('sessionSetup.backToExam')}
        </button>
        <h2 className="section-title" style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          {t('sessionSetup.pageTitle')}
          <span className="text-sm text-muted" style={{ fontWeight: 400 }}>{detail.session_name}</span>
        </h2>
      </div>

      {/* Status summary row */}
      <div className="setup-status-row">
        <span
          className={`chip ${hasRubric ? "chip-check" : "chip-missing"}`}
        >
          {hasRubric ? (
            <CheckCircle2 size={14} />
          ) : (
            <AlertCircle size={14} />
          )}
          {t('sessionSetup.rubricChip')}
        </span>
        <span
          className={`chip ${detail.questions.length > 0 ? "chip-check" : "chip-missing"}`}
        >
          {detail.questions.length > 0 ? (
            <CheckCircle2 size={14} />
          ) : (
            <AlertCircle size={14} />
          )}
          {t('sessionSetup.questionsChip')} {detail.questions.length}
        </span>
        <span className="chip chip-papers">
          {detail.papers.length} /{" "}
          {classStudentsStatus === "loading"
            ? "…"
            : classStudentsStatus === "error"
              ? "?"
              : classStudents.length || detail.papers.length || 0}{" "}
          {t('sessionSetup.papersSuffix', { count: classStudents.length || detail.papers.length || 0 })}
        </span>
      </div>

      {/* Tab navigation */}
      <div className="tabs mb-0">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
            {t.count !== undefined && (
              <span className="tab-count">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="setup-panel animate-fade-in">
        {/* ── RUBRIC TAB ── */}
        {activeTab === "rubric" && (
          <div className="animate-fade-in">
            <p className="text-sm text-muted mb-20">
              {t('sessionSetup.rubricTabDesc')}
            </p>

            {hasRubric && (
              <div className="upload-file-row">
                <div className="upload-file-icon">
                  <FileText size={16} />
                </div>
                <span className="chip chip-check">
                  <CheckCircle2 size={13} />
                  {t('sessionSetup.rubricLoaded')}
                </span>
                <button
                  className="btn btn-icon btn-ghost"
                  onClick={removeRubric}
                  title={t('sessionSetup.removeFileTitle')}
                  style={{ marginLeft: "auto" }}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Rubric preview */}
            {detail.rubric_preview && (
              <div className="card card-padding mb-20 text-sm">
                {detail.rubric_preview.summary && (
                  <p style={{ marginBottom: 8 }}>
                    {detail.rubric_preview.summary}
                  </p>
                )}
                {detail.rubric_preview.visual_metadata_text && (
                  <p className="text-muted" style={{ marginBottom: 8, fontStyle: "italic" }}>
                    {detail.rubric_preview.visual_metadata_text}
                  </p>
                )}
                {detail.rubric_preview.criteria?.length > 0 && (
                  <ul style={{ paddingLeft: 16, margin: 0 }}>
                    {detail.rubric_preview.criteria.map((c, i) => (
                      <li key={i} style={{ marginBottom: 10 }}>
                        <strong>{c.name}</strong>
                        {c.expectation && c.expectation !== c.name && (
                          <>: {c.expectation}</>
                        )}
                        {c.weight_or_points && (
                          <span className="text-muted">
                            {" "}
                            ({c.weight_or_points})
                          </span>
                        )}
                        {c.reference_answer && (
                          <div className="text-muted" style={{ marginTop: 4 }}>
                            <strong>{t('common.referenceAnswer')}</strong>{" "}
                            {c.reference_answer}
                          </div>
                        )}
                        {c.visual_metadata_text && (
                          <div className="text-muted" style={{ marginTop: 2, fontStyle: "italic" }}>
                            {c.visual_metadata_text}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div
              className={`upload-zone ${rubricDragging ? "dragging" : ""} ${hasRubric ? "upload-zone-replace" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploadLocked) setRubricDragging(true);
              }}
              onDragLeave={() => setRubricDragging(false)}
              onDrop={handleRubricDrop}
              onClick={() => !uploadLocked && rubricInputRef.current?.click()}
              style={uploadLocked ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "auto" } : undefined}
            >
              <input
                ref={rubricInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (!uploadLocked && e.target.files?.[0]) uploadRubric(e.target.files[0]);
                }}
                disabled={uploadLocked}
              />
              {uploadLocked ? (
                <>
                  <Loader2 size={28} className="upload-zone-icon spin" />
                  <p className="upload-zone-label">{t('sessionSetup.uploadInProgress')}</p>
                  <p className="upload-zone-hint">
                    {t('sessionSetup.uploadInProgressHint')}
                  </p>
                </>
              ) : (
                <>
                  <Upload size={28} className="upload-zone-icon" />
                  <p className="upload-zone-label">
                    {hasRubric
                      ? t('sessionSetup.dropOrClick')
                      : t('sessionSetup.rubricDropLabel')}
                  </p>
                  <p className="upload-zone-hint">
                    {t('sessionSetup.rubricFileHint')}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── QUESTIONS TAB ── */}
        {activeTab === "questions" && (
          <div className="animate-fade-in">
            <input
              ref={questionsInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file && !uploadLocked) uploadQuestionPaper(file);
              }}
              disabled={uploadLocked}
            />

            {localQuestions.length > 0 ? (
              <>
                {/* Question paper status + actions */}
                <div
                  className={`upload-file-row question-file-row ${questionsDragging ? "dragging" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!uploadLocked) setQuestionsDragging(true);
                  }}
                  onDragLeave={() => setQuestionsDragging(false)}
                  onDrop={handleQuestionsDrop}
                >
                  <div className="upload-file-icon">
                    <FileText size={16} />
                  </div>
                  <span className="chip chip-check">
                    <CheckCircle2 size={13} />
                    {t('sessionSetup.questionsLoaded')}
                  </span>

                  <div className="question-toolbar">
                    {questionsModified && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={saveQuestions}
                        disabled={questionsSaving}
                      >
                        {questionsSaving ? t('common.saving') : t('sessionSetup.saveQuestions')}
                      </button>
                    )}
                    {questionsApproved ? (
                      <>
                        <span className="question-approved-label">
                          {t('sessionSetup.questionsApproved')}
                        </span>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={handleUnapproveQuestions}
                        >
                          {t('sessionSetup.revokeApproval')}
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-sm btn-approve"
                        onClick={handleApproveQuestions}
                      >
                        {t('sessionSetup.approveQuestions')}
                      </button>
                    )}
                    <button
                      className="btn btn-icon btn-ghost"
                      onClick={clearQuestions}
                      disabled={uploadLocked}
                      title={t('sessionSetup.removeQuestions')}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-12 mt-12">
                  {localQuestions.map((q, idx) => (
                    <div key={q.id ?? idx} className="question-row">
                      <span className="question-row-num">
                        {t('sessionDetail.questionPrefix')}{q.question_number}
                      </span>
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div className="question-row-body">
                          <textarea
                            className="form-input"
                            style={{ resize: "vertical", minHeight: 60 }}
                            value={q.question_text ?? ""}
                             placeholder={t('sessionSetup.questionTextPlaceholder')}
                            onChange={e => updateLocalQuestion(idx, { question_text: e.target.value })}
                          />
                          <div className="question-row-pts">
                            <span className="text-xs text-muted">{t('sessionSetup.maxPointsLabel')}</span>
                            <input
                              type="number"
                              className="form-input"
                              style={{ width: 72 }}
                              value={(q.maximum_points ?? q.max_points) ?? ""}
                              onChange={e => updateLocalQuestion(idx, { maximum_points: e.target.value === "" ? null : Number(e.target.value) })}
                            />
                          </div>
                        </div>

                        {/* Sub-questions */}
                        {(q.sub_questions ?? []).length > 0 && (
                          <div
                            style={{
                              borderLeft: "3px solid var(--color-accent, #e09a3a)",
                              paddingLeft: 12,
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {(q.sub_questions ?? []).map((sq, sqIdx) => (
                              <div
                                key={sqIdx}
                                style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
                              >
                                <input
                                  type="text"
                                  className="form-input"
                                  style={{ width: 52, flexShrink: 0, fontWeight: 600, textAlign: "center" }}
                                  value={sq.label}
                                  placeholder="a)"
                                  onChange={e => updateLocalSubQuestion(idx, sqIdx, { label: e.target.value })}
                                />
                                <textarea
                                  className="form-input"
                                  style={{ flex: 1, resize: "vertical", minHeight: 48 }}
                                  value={sq.text}
                                  placeholder={t('sessionSetup.subQuestionPlaceholder')}
                                  onChange={e => updateLocalSubQuestion(idx, sqIdx, { text: e.target.value })}
                                />
                                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center", flexShrink: 0 }}>
                                  <span className="text-xs text-muted">{t('sessionSetup.pointsLabel')}</span>
                                  <input
                                    type="number"
                                    className="form-input"
                                    style={{ width: 64 }}
                                    value={sq.maximum_points ?? ""}
                                    onChange={e => updateLocalSubQuestion(idx, sqIdx, { maximum_points: e.target.value === "" ? null : Number(e.target.value) })}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div
                className={`upload-zone ${questionsDragging ? "dragging" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!uploadLocked) setQuestionsDragging(true);
                }}
                onDragLeave={() => setQuestionsDragging(false)}
                onDrop={handleQuestionsDrop}
                onClick={() => !uploadLocked && questionsInputRef.current?.click()}
                style={uploadLocked ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "auto" } : undefined}
              >
                {uploadLocked ? (
                  <>
                    <Loader2 size={28} className="upload-zone-icon spin" />
                    <p className="upload-zone-label">{t('sessionSetup.uploadInProgress')}</p>
                    <p className="upload-zone-hint">
                      {t('sessionSetup.uploadInProgressHint')}
                    </p>
                  </>
                ) : (
                  <>
                    <Upload size={28} className="upload-zone-icon" />
                    <p className="upload-zone-label">
                      {t('sessionSetup.questionPaperUploadLabel')}
                    </p>
                    <p className="upload-zone-hint">
                      {t('sessionSetup.questionPaperFileHint')}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── PAPERS TAB ── */}
        {activeTab === "papers" && (
          <div className="animate-fade-in">
            <p className="text-sm text-muted mb-20">
              {t('sessionSetup.papersTabDesc')}
            </p>

            {/* Student selection */}
            {detail.class_id ? (
              <div className="form-group mb-20">
                <label className="form-label">
                  {t('sessionSetup.assignPapersLabel')}
                </label>
                <select
                  className="form-select"
                  value={selectedStudentId ?? ""}
                  onChange={(e) =>
                    setSelectedStudentId(Number(e.target.value) || null)
                  }
                  disabled={
                    studentsWithoutSubmission.length === 0 ||
                    classStudentsStatus === "loading"
                  }
                >
                  {classStudentsStatus === "loading" && (
                    <option value="">{t('sessionSetup.studentsLoading')}</option>
                  )}
                  {classStudentsStatus === "error" && (
                    <option value="">{t('sessionSetup.studentsLoadError')}</option>
                  )}
                  {classStudentsStatus === "loaded" &&
                    classStudents.length === 0 && (
                        <option value="">
                          {t('sessionSetup.noStudentsInClass')}
                        </option>
                    )}
                  {classStudentsStatus === "loaded" &&
                    classStudents.length > 0 &&
                    studentsWithoutSubmission.length === 0 && (
                        <option value="">
                          {t('sessionSetup.allStudentsLoaded')}
                        </option>
                    )}
                  {studentsWithoutSubmission.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.full_name} ({student.student_no})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="card card-padding mb-20">
                <p className="text-sm text-muted">
                  {t('sessionSetup.noClassLinked')}
                </p>
              </div>
            )}

            {/* Upload zone */}
            <div
              className={`upload-zone ${papersDragging ? "dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploadLocked) setPapersDragging(true);
              }}
              onDragLeave={() => setPapersDragging(false)}
              onDrop={handlePapersDrop}
              onClick={() =>
                !uploadLocked && selectedStudentId && papersInputRef.current?.click()
              }
              style={{
                marginBottom: 20,
                opacity: uploadLocked || !selectedStudentId ? 0.5 : 1,
                cursor: uploadLocked || !selectedStudentId ? "not-allowed" : "pointer",
              }}
            >
              <input
                ref={papersInputRef}
                type="file"
                accept="image/*,.pdf"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  if (!uploadLocked) uploadForSelectedStudent(e.target.files);
                }}
                disabled={uploadLocked || !selectedStudentId}
              />
              {uploadLocked ? (
                <>
                  <Loader2 size={28} className="upload-zone-icon spin" />
                  <p className="upload-zone-label">{t('sessionSetup.uploadInProgress')}</p>
                  <p className="upload-zone-hint">
                    {t('sessionSetup.uploadInProgressHint')}
                  </p>
                </>
              ) : (
                <>
                  <Upload size={28} className="upload-zone-icon" />
                  <p className="upload-zone-label">
                    {selectedStudentId
                      ? t('sessionSetup.paperDropLabel')
                      : t('sessionSetup.paperDropDisabled')}
                  </p>
                  <p className="upload-zone-hint">
                    {t('sessionSetup.paperFileHint')}
                  </p>
                </>
              )}
            </div>

            {/* Papers list */}
            {detail.papers.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-12 mt-20">
                  {t('sessionSetup.uploadedPapers')}
                </h4>
                <div className="papers-list">
                  {detail.papers.map((p: Paper) => (
                    <div key={p.id} className="upload-file-row">
                      <div className="upload-file-icon upload-file-icon-sm">
                        <FileText size={16} />
                      </div>
                      <div className="upload-file-info">
                        <div className="upload-file-name-compact">
                          <span className="upload-file-student">
                            {p.student_name}
                          </span>
                          <span className="upload-file-filename">
                            {p.original_filename}
                          </span>
                        </div>
                      </div>
                      <a
                        className="btn btn-outline btn-sm"
                        href={`/stream_file/${p.file_id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: "0.75rem" }}
                      >
                        {t('sessionSetup.openPaper')}
                      </a>
                      <button
                        className="btn btn-icon btn-ghost"
                        onClick={() => deletePaper(p.id)}
                        title={t('sessionSetup.removePaper')}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.papers.length === 0 && (
              <div
                className="text-sm text-muted mt-16"
                style={{ textAlign: "center" }}
              >
                {t('sessionSetup.noPapersUploaded')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionSetupPage;
